"use client";

import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { NotificationChannelView } from "@/hooks/notifications/use-notification-channels";
import { useTranslations } from "next-intl"


const BOTFATHER_URL = "https://t.me/BotFather";

interface TelegramConnectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConnect: (name: string, botToken: string) => Promise<NotificationChannelView>;
  /** Called with the freshly-connected channel so the parent can open enrollment. */
  onConnected: (channel: NotificationChannelView) => void;
}

export function TelegramConnectDialog({
  open,
  onOpenChange,
  onConnect,
  onConnected,
}: TelegramConnectDialogProps) {
  const tUi = useTranslations("NotificationChannels.NotificationChannelsTelegramConnectDialog")
  const tCommonUi = useTranslations("Common")

  const [name, setName] = useState("");
  const [botToken, setBotToken] = useState("");
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (open) {
      setName("");
      setBotToken("");
    }
  }, [open]);

  const canConnect = name.trim().length > 0 && botToken.trim().length > 0 && !connecting;

  const handleConnect = async () => {
    if (!canConnect) return;
    setConnecting(true);
    try {
      const channel = await onConnect(name.trim(), botToken.trim());
      onOpenChange(false);
      onConnected(channel);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to connect Telegram bot");
    } finally {
      setConnecting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle><span key={tUi("connectATelegramBot")}>{tUi("connectATelegramBot")}</span></DialogTitle>
          <DialogDescription>
            <span key={tUi("aDedicatedTelegramBotDeliversEscalationAlertsTo")}>{tUi("aDedicatedTelegramBotDeliversEscalationAlertsTo")}</span></DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <ol className="list-decimal space-y-1.5 rounded-md bg-muted/50 py-3 pl-8 pr-4 text-sm text-muted-foreground">
            <li>
              <span><span key={tUi("open")}>{tUi("open")}</span></span>{" "}
              <a
                href={BOTFATHER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary underline underline-offset-2"
              >
                <span key={"@BotFather"}>{"@BotFather"}</span></a>{" "}
              <span><span key={tUi("inTelegramAndSend")}>{tUi("inTelegramAndSend")}</span></span>{" "}
              <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">/newbot</code><span>.</span>
            </li>
            <li><span key={tUi("followThePromptsToPickANameAnd")}>{tUi("followThePromptsToPickANameAnd")}</span></li>
            <li>
              <span><span key={tUi("botFatherRepliesWithAnAPI")}>{tUi("botFatherRepliesWithAnAPI")}</span></span> <strong><span key={tUi("token")}>{tUi("token")}</span></strong> <span><span key={tUi("like")}>{tUi("like")}</span></span>{" "}
              <code className="font-mono text-xs">123456:ABC-DEF…</code><span><span key={tUi("copyIt")}>{tUi("copyIt")}</span></span>
            </li>
            <li><span><span key={tUi("pasteTheTokenBelowAndClick")}>{tUi("pasteTheTokenBelowAndClick")}</span></span> <strong><span key={tUi("connect")}>{tUi("connect")}</span></strong><span>.</span></li>
            <li>
              <span key={tUi("onTheNextStepScanTheQRCode")}>{tUi("onTheNextStepScanTheQRCode")}</span></li>
          </ol>

          <div className="space-y-1.5">
            <Label htmlFor="nc-tg-name"><span key={tUi("name")}>{tUi("name")}</span></Label>
            <Input
              id="nc-tg-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={"Ops Telegram"}
              maxLength={100}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="nc-tg-token"><span key={tUi("botToken")}>{tUi("botToken")}</span></Label>
            <Input
              id="nc-tg-token"
              value={botToken}
              onChange={(e) => setBotToken(e.target.value)}
              placeholder={"123456:ABC-DEF…"}
              autoComplete="off"
            />
            <p className="text-xs text-muted-foreground">
              <span><span key={tUi("from")}>{tUi("from")}</span></span>{" "}
              <a
                href={BOTFATHER_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary underline underline-offset-2"
              >
                <span key={"@BotFather"}>{"@BotFather"}</span></a>
              <span><span key={tUi("storedEncryptedNeverShownAgain")}>{tUi("storedEncryptedNeverShownAgain")}</span></span>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={connecting}>
            <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
          <Button onClick={handleConnect} disabled={!canConnect}>
            <span key={connecting ? tUi("connecting") : tUi("connect")}>{connecting ? tUi("connecting") : tUi("connect")}</span>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
